import React, { Component } from "react"
import { connect } from "react-redux"
import styled, { css } from 'styled-components'
import PageTransition from 'gatsby-plugin-page-transitions';
import { rgba } from 'polished'
import Form from 'react-dynamic-form'

import { Section, Input } from '../components'

import { fetchSettings } from '../store/settings'

import { pageTransitionProps } from '../utils'
import { hoverState } from "../styles/global"
import { paragraph, caption } from '../styles/type'
import { media } from '../styles/utils'
import { grey } from '../styles/colors'
import { email } from '../utils/validators'
import { NEWSLETTER_WEBHOOK_URL } from '../constants'


const POPUP_SESSION_KEY = 'homeNewsletterPopupSeen'
const POPUP_SCROLL_THRESHOLD = 0.5


const ArrowRightIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 12H19M13 6L19 12L13 18" stroke="#4D402E" strokeLinecap="square" strokeLinejoin="round"/>
    </svg>
)


class Home extends Component {

    state = {
        acf: null,
        popupActive: false,
        newsletterComplete: false,
        newsletterError: null,
    }

    constructor(props) {
        super(props)
        this.newsletterForm = React.createRef()
    }

    componentWillMount = () => {
        this.props.fetchSettings()
        const data = this.props.data.wordpressHome
        this.setState({
            acf: JSON.parse(data.acf_json),
        })
    }

    componentDidMount = () => {
        let seen = false
        try { seen = !!sessionStorage.getItem(POPUP_SESSION_KEY) } catch (e) {}
        if (seen) return

        this.scrollTicking = false
        window.addEventListener('scroll', this.onScroll, { passive: true })
    }

    componentWillUnmount = () => {
        window.removeEventListener('scroll', this.onScroll)
    }

    onScroll = () => {
        if (this.scrollTicking) return
        this.scrollTicking = true
        window.requestAnimationFrame(this.checkScrollThreshold)
    }

    checkScrollThreshold = () => {
        this.scrollTicking = false
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const docHeight = document.documentElement.scrollHeight - window.innerHeight
        const progress = docHeight > 0 ? scrollTop / docHeight : 0

        if (progress >= POPUP_SCROLL_THRESHOLD) {
            this.showPopup()
        }
    }

    showPopup = () => {
        try { sessionStorage.setItem(POPUP_SESSION_KEY, '1') } catch (e) {}
        window.removeEventListener('scroll', this.onScroll)
        this.setState({ popupActive: true })
    }

    dismissPopup = () => {
        this.setState({ popupActive: false })
    }

    handleNewsletterSubmit = (fields, resetForm) => {
        const emailValue = fields['email'].value

        const body = new URLSearchParams()
        body.append('email', emailValue)
        body.append('source', 'homepage_popup')

        fetch(NEWSLETTER_WEBHOOK_URL, {
            method: 'POST',
            body,
        }).then((r) => {
            if (r.ok) {
                this.setState({ newsletterComplete: true, newsletterError: null })
                resetForm()
                setTimeout(this.dismissPopup, 2500)
            } else {
                this.setState({ newsletterError: 'Something went wrong. Please try again.' })
            }
        }).catch(() => {
            this.setState({ newsletterError: 'Something went wrong. Please try again.' })
        })
    }

    renderSections = () => {
        const { acf } = this.state
        if (!acf || !acf.sections) return null

        // Contact + subscribe now live in the global FooterBlock, so filter
        // them out of the homepage sections to avoid duplicate content.
        const HIDDEN_LAYOUTS = ['contact', 'sign_up']

        return acf.sections.map((section, i) => {
            if (!section.display_section || !section.blocks) return null

            const visibleBlocks = section.blocks.filter(
                (b) => HIDDEN_LAYOUTS.indexOf(b.acf_fc_layout) === -1
            )
            if (visibleBlocks.length === 0) return null

            return (
                <Section
                    key={i}
                    blocks={visibleBlocks}
                    section={section}
                />
            )
        })
    }

    renderPopup = () => {
        const { popupActive, newsletterComplete, newsletterError } = this.state

        const newsletterFields = {
            fields: [
                {
                    type: 'text',
                    name: 'email',
                    label: 'Email Address',
                    validator: email,
                    errorMessage: 'Please enter a valid email address',
                },
            ]
        }

        return (
            <Popup active={popupActive}>
                <PopupCard>
                    <PopupClose onClick={this.dismissPopup} aria-label="Close">×</PopupClose>

                    {newsletterComplete ? (
                        <PopupThanks>Thanks — you're subscribed.</PopupThanks>
                    ) : (
                        <React.Fragment>
                            <PopupHeading>Stay Connected</PopupHeading>
                            <PopupCopy>
                                Subscribe to receive occasional updates from the studio, including recent projects, publications and news from Paul Conrad Architects.
                            </PopupCopy>

                            <PopupFormRow>
                                <PopupFormInputWrap>
                                    <Form
                                        ref={this.newsletterForm}
                                        data={newsletterFields}
                                        styles={popupFormStyles}
                                        renderInput={<Input />}
                                        renderSubmit={false}
                                        onSubmit={this.handleNewsletterSubmit}
                                        moveToInvalidField={false}
                                    />
                                </PopupFormInputWrap>
                                <PopupArrow
                                    onClick={() => {
                                        this.newsletterForm.current && this.newsletterForm.current.handleSubmit()
                                    }}
                                    aria-label="Subscribe"
                                >
                                    <ArrowRightIcon />
                                </PopupArrow>
                            </PopupFormRow>

                            {newsletterError && (
                                <PopupError>{newsletterError}</PopupError>
                            )}
                        </React.Fragment>
                    )}
                </PopupCard>
            </Popup>
        )
    }

    render() {
        return (
            <React.Fragment>
                <PageTransition {...pageTransitionProps}>
                    <Wrapper>
                        {this.renderSections()}
                    </Wrapper>
                </PageTransition>

                {this.renderPopup()}
            </React.Fragment>
        )
    }
}


const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    z-index: 2;
`


// Homepage newsletter popup

const Popup = styled.div`
    position: fixed;
    right: 3rem;
    bottom: 3rem;
    z-index: 12;
    transition: opacity 0.4s ease, transform 0.4s ease;
    opacity: ${props => (props.active ? 1 : 0)};
    transform: ${props => (props.active ? 'translateY(0)' : 'translateY(20px)')};
    pointer-events: ${props => (props.active ? 'auto' : 'none')};

    ${media.phone`
        right: 20px;
        left: 20px;
        bottom: 20px;
    `}

    /* Opt out of the site-wide "indent subsequent paragraphs" rule */
    * {
        text-indent: 0 !important;
    }
`

const PopupCard = styled.div`
    position: relative;
    width: 380px;
    max-width: 100%;
    background: #EDEDEB;
    padding: 4rem;
    box-sizing: border-box;
    box-shadow: 0 8px 24px ${rgba('#000000', 0.08)};

    ${media.phone`
        width: 100%;
        padding: 3rem;
    `}
`

const PopupClose = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    background: transparent;
    border: 0;
    padding: 4px 8px;
    font-size: 24px;
    line-height: 1;
    color: ${grey};
    cursor: pointer;
    ${hoverState};
`

const PopupHeading = styled.div`
    font-family: 'Scto';
    font-weight: 300;
    font-size: 22px;
    line-height: 28px;
    color: ${grey};
    margin-bottom: 2rem;

    ${media.laptop`
        font-size: 18px;
        line-height: 24px;
    `}
`

const PopupCopy = styled.div`
    ${paragraph};
    color: ${grey};
    margin-bottom: 3rem;
`

const PopupFormRow = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    border-bottom: 1px solid ${rgba('#595850', 0.4)};

    .dynamic-field { margin-bottom: 0 !important; }
    .field-wrapper {
        border-bottom: 0 !important;
        margin-bottom: 0 !important;
    }
`

const PopupFormInputWrap = styled.div`
    flex: 1;
    min-width: 0;
`

const PopupArrow = styled.button`
    background: transparent;
    border: 0;
    padding: 0;
    margin-left: 16px;
    display: flex;
    align-items: center;
    cursor: pointer;

    svg { display: block; }
    &:hover { opacity: 0.7; }
`

const PopupThanks = styled.div`
    ${paragraph};
    color: ${grey};
`

const PopupError = styled.div`
    ${caption};
    color: #F03232;
    margin-top: 12px;
`

const popupFormStyles = css`
    .dynamic-field {
        margin-bottom: 0;
        .field-wrapper {
            border-bottom: 0;
            margin-bottom: 0;
        }
    }
`


const mapDispatchToProps = dispatch => ({
    fetchSettings() {
        dispatch(fetchSettings())
    },
})

const mapStateToProps = (state, props) => ({
    settings: state.settings.settings
})

export default connect(mapStateToProps, mapDispatchToProps)(Home);


export const pageQuery = graphql`
    query Home {
        wordpressHome {
            acf_json
        }
    }
`



// WEBPACK FOOTER //
// ./src/pages/index.js